import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react/macro';
import { parseOauthAuthorizeRequest } from 'modules/auth/domain/utils/oauth-authorize-request';
import { Pages } from 'modules/navigation/domain/enums/pages';
import React, { useCallback, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useToastApiError } from 'shared/hooks/use-toast-api-error';
import UDButton from 'ud-ui/components/button';
import UDText from 'ud-ui/components/text';
import { OauthRedirectMismatchError, useOauthConsent } from '../hooks/use-oauth-consent';
import AuthSessionPending from '../components/auth-session-pending';
import AuthLayout from '../layouts';

/** Бэкенд выдаёт код только владельцу `client_id`, чужому аккаунту — 403. */
const FORBIDDEN = 403;

/**
 * Согласие на доступ AI-клиента (MCP) к ImGame. Что показываем — по MCP Security Best Practices:
 * куда уйдёт доступ (хост `redirect_uri`), что он даёт и под каким аккаунтом.
 * `client_id` не показываем: у бэкенда это телефон пользователя, а не название клиента.
 */
const OauthConsentPage = () => {
  const { t } = useLingui();
  const toastApiError = useToastApiError();
  const { search } = useLocation();
  const request = useMemo(() => parseOauthAuthorizeRequest(search), [search]);
  const { user, allow, deny, switchAccount } = useOauthConsent(request);
  // После «Разрешить» / «Отказать» браузер уходит со страницы — кнопки больше не нажимаются.
  const [isLeaving, setIsLeaving] = useState(false);

  const onAllow = useCallback(
    () =>
      allow()
        .then(() => setIsLeaving(true))
        .catch((error) => {
          if (error instanceof OauthRedirectMismatchError) {
            toast.error(t`Не удалось разрешить доступ: сервер вернул чужой адрес возврата`);
          } else if ((error as { status?: number }).status === FORBIDDEN) {
            // Бэкенд выдаёт код только владельцу `client_id` (телефон), чужому — `403 Unauthorized`.
            toast.error(t`Подключение начато для другого аккаунта ImGame. Войдите под ним.`);
          } else {
            toastApiError(error, msg`Не удалось разрешить доступ`);
          }
        }),
    [allow, t, toastApiError],
  );

  const onDeny = useCallback(() => {
    setIsLeaving(true);
    deny();
  }, [deny]);

  if (!request) {
    return (
      <AuthLayout logoTo={Pages.INDEX} title={t`Подключение не удалось`}>
        <UDText type="callout" className="text-inkOnDark">
          {t`В ссылке на подключение не хватает данных. Начните подключение заново в приложении, из которого пришли.`}
        </UDText>
      </AuthLayout>
    );
  }

  // Сессия ещё проверяется: без входа `authCheck` сам уведёт на `/sign-in`.
  if (!user) {
    return (
      <AuthLayout logoTo={Pages.INDEX} title={t`Подключение к ImGame`}>
        <AuthSessionPending />
      </AuthLayout>
    );
  }

  const account = [user.name, user.surname].filter(Boolean).join(' ');
  const phone = user.phone;

  return (
    <AuthLayout logoTo={Pages.INDEX} title={t`Подключение к ImGame`}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <UDText type="title-1" className="text-inkOnDark break-all">
            {request.redirectHost}
          </UDText>
          <UDText type="callout" className="text-inkOnDark">
            {t`запрашивает доступ к вашему аккаунту ImGame`}
          </UDText>
        </div>

        <div className="flex flex-col gap-2">
          <UDText type="subhead" className="text-inkOnDark">
            {t`Приложение сможет действовать в ImGame от вашего имени: смотреть и менять данные, к которым у вас есть доступ.`}
          </UDText>
          <UDText type="subhead" className="text-inkOnDark">
            {account ? t`Аккаунт: ${account}, ${phone}` : t`Аккаунт: ${phone}`}
          </UDText>
          <UDText type="footnote" className="text-inkOnDark">
            {t`Разрешайте, только если сами начали подключение в этом приложении.`}
          </UDText>
        </div>

        <div className="flex flex-col gap-2">
          <div className="flex gap-3">
            <UDButton variant="dark" className="flex-1" onClick={onDeny} disabled={isLeaving}>
              {t`Отказать`}
            </UDButton>
            <UDButton variant="primary" className="flex-1" onClick={onAllow} disabled={isLeaving}>
              {t`Разрешить`}
            </UDButton>
          </div>
          <UDButton variant="tertiary" subVariant="for-dark" onClick={switchAccount} disabled={isLeaving}>
            {t`Войти под другим аккаунтом`}
          </UDButton>
        </div>
      </div>
    </AuthLayout>
  );
};

export default OauthConsentPage;
